import React from 'react';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import { Stethoscope, CheckCircle } from 'lucide-react';
export default function Specialist() {
  return <main className="min-h-screen bg-white" data-id="element-1205">
      <Helmet data-id="element-1206">
        <title data-id="element-1207">
          Psychiatric Reports for Courts & Tribunals | Medico-Legal Psychiatry
        </title>
        <meta name="description" content="Expert psychiatric reports for courts & tribunals by Dr. Imtiaz Syed. Specialist evaluations for immigration, family, employment, civil and criminal cases." data-id="element-1208" />
        <meta name="keywords" content="Psychiatric Reports for Courts & Tribunals, Psychiatric Expert for Solicitors and Barristers, Immigration Tribunals, Criminal Courts, Family Courts, Employment Tribunals, Expert Witness Psychiatrist, Psychiatric expert witness for parole board hearings" data-id="element-1209" />
        <script type="application/ld+json" data-id="element-1210">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Psychiatric Reports for Courts & Tribunals",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Specialist Psychiatric Evaluation",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert Psychiatric Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1211">
        <div className="container mx-auto px-4" data-id="element-1212">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1213">
            Psychiatric Reports for Courts & Tribunals
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1214">
            Tailored Expertise for Diverse Legal Needs
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1215">
        <div className="container mx-auto px-4" data-id="element-1216">
          <div className="max-w-3xl mx-auto" data-id="element-1217">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1218">
              The role of expert psychiatric opinion extends across a vast array
              of legal contexts, each with unique requirements. Dr. Imtiaz Syed
              provides highly specialised psychiatric evaluations tailored to
              the nuances of diverse legal settings, offering clarity and
              precise insights where mental health intersects with the law. His
              expertise ensures that the complexities of psychological factors
              are meticulously presented and understood, supporting informed
              decisions in various legal arenas.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1219">
              Why Instruct Dr. Imtiaz Syed for Specialist Evaluations?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1220">
              Dr. Syed's broad expertise across multiple legal domains ensures:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1221">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1222">
                <div className="flex items-start" data-id="element-1223">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1224" />
                  <div data-id="element-1225">
                    <h3 className="font-bold text-primary" data-id="element-1226">
                      Context-Specific Understanding
                    </h3>
                    <p className="text-slate" data-id="element-1227">
                      Reports are tailored to the specific legal questions and
                      frameworks of each tribunal or court.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1228">
                <div className="flex items-start" data-id="element-1229">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1230" />
                  <div data-id="element-1231">
                    <h3 className="font-bold text-primary" data-id="element-1232">
                      Comprehensive Coverage
                    </h3>
                    <p className="text-slate" data-id="element-1233">
                      Addressing mental health complexities in immigration,
                      family, employment, civil, and review tribunals.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1234">
                <div className="flex items-start" data-id="element-1235">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1236" />
                  <div data-id="element-1237">
                    <h3 className="font-bold text-primary" data-id="element-1238">
                      Experienced Courtroom Presence
                    </h3>
                    <p className="text-slate" data-id="element-1239">
                      Providing robust and clear expert testimony in diverse
                      legal settings.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1240">
                <div className="flex items-start" data-id="element-1241">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1242" />
                  <div data-id="element-1243">
                    <h3 className="font-bold text-primary" data-id="element-1244">
                      Nuanced Psychiatric Insights
                    </h3>
                    <p className="text-slate" data-id="element-1245">
                      Illuminating the interplay between mental health
                      conditions and legal outcomes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1246">
              Key Specialist Psychiatric Evaluations Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1247">
              Dr. Syed offers expert opinions for, but not limited to:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1248">
              <li className="flex items-start" data-id="element-1249">
                <span className="text-secondary font-bold mr-2" data-id="element-1250">•</span>
                <span data-id="element-1251">
                  <strong data-id="element-1252">Immigration and Asylum Tribunals:</strong> Assessing
                  the psychological impact of removal or deportation, and
                  evaluating potential risks upon return to an individual's home
                  country.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1253">
                <span className="text-secondary font-bold mr-2" data-id="element-1254">•</span>
                <span data-id="element-1255">
                  <strong data-id="element-1256">Criminal Courts:</strong> Expert psychiatric insights
                  relevant to criminal proceedings, beyond fitness to plead.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1257">
                <span className="text-secondary font-bold mr-2" data-id="element-1258">•</span>
                <span data-id="element-1259">
                  <strong data-id="element-1260">Family Courts:</strong> Expert opinions on parenting
                  capacity, domestic abuse, child protection, and other family
                  law matters.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1261">
                <span className="text-secondary font-bold mr-2" data-id="element-1262">•</span>
                <span data-id="element-1263">
                  <strong data-id="element-1264">Employment Tribunals:</strong> Evaluating mental
                  health impacts in workplace disputes, discrimination, or
                  personal injury claims related to employment.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1265">
                <span className="text-secondary font-bold mr-2" data-id="element-1266">•</span>
                <span data-id="element-1267">
                  <strong data-id="element-1268">Civil Claims and Personal Injury:</strong> Assessing
                  the psychiatric consequences of civil wrongs or injuries,
                  including prognosis and treatment recommendations.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1269">
                <span className="text-secondary font-bold mr-2" data-id="element-1270">•</span>
                <span data-id="element-1271">
                  <strong data-id="element-1272">Mental Health Review Tribunals:</strong> Providing
                  expert psychiatric assessments to inform decisions regarding
                  detention under the Mental Health Act.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1273">
                <span className="text-secondary font-bold mr-2" data-id="element-1274">•</span>
                <span data-id="element-1275">
                  <strong data-id="element-1276">Parole Board Hearings:</strong> Offering expert
                  evaluations to inform decisions regarding release from
                  custody, focusing on risk and mental state.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1277">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1278">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed offers both secure remote assessments across the UK and
              in-person consultations when appropriate, including visits to
              specific institutions as required. The resulting reports are
              always clear, legally relevant, and provide precise findings and
              actionable insights for your case, designed to withstand scrutiny.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1279">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1280">
              Dr. Imtiaz Syed is your trusted expert for navigating the
              intricate connections between mental health and the law across
              various legal landscapes. His clear, objective, and timely
              evaluations are invaluable in achieving just and informed
              decisions for your clients.
            </p>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1281">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1282">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1283">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1284">
                <span className="mr-2 text-slate-dark" data-id="element-1285">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1286">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1287">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1288">
                Book a Specialist Psychiatric Evaluation
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}